import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TransactionDetails from './components/TransactionDetails';
import DocumentVerification from './components/DocumentVerification';
import ComplianceChecks from './components/ComplianceChecks';
import TransactionTimeline from './components/TransactionTimeline';
import ReviewActions from './components/ReviewActions';
import CustomerHistory from './components/CustomerHistory';

const TransactionReview = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock admin user data
  const mockUser = {
    id: 'admin_001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1de883763-1763295152911.png",
    avatarAlt: 'Professional headshot of woman with shoulder-length brown hair in business attire'
  };

  // Mock transaction data for review
  const mockTransaction = {
    id: 'TXN-2024-001847',
    status: 'compliance_review',
    submittedAt: '2024-11-18T09:30:00Z',
    sender: {
      id: 'USR-001234',
      name: 'Michael Rodriguez',
      email: 'michael.rodriguez@email.com',
      phone: '+1-555-0123',
      country: 'United States',
      kycStatus: 'verified'
    },
    recipient: {
      id: 'RCP-005678',
      name: 'Elena Popescu',
      bank: 'Banca Transilvania',
      iban: 'RO49AAAA1B31007593840000',
      swift: 'BTRLRO22',
      country: 'Romania'
    },
    sendAmount: 5000,
    sendCurrency: 'USD',
    receiveAmount: 23450,
    receiveCurrency: 'RON',
    exchangeRate: 4.69,
    fees: 25.00,
    transferType: 'standard',
    sourceOfFunds: 'Salary',
    transferPurpose: 'Family Support',
    riskScore: 75
  };

  // Mock documents data
  const mockDocuments = [
  {
    id: 'DOC-001',
    type: 'Passport',
    filename: 'passport_michael_rodriguez.pdf',
    status: 'verified',
    quality: 'high',
    size: '2.4 MB',
    uploadedAt: '2024-11-17T14:20:00Z',
    expiryDate: '2029-03-15T00:00:00Z',
    documentNumber: 'US123456789',
    issueDate: '2019-03-15T00:00:00Z',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1067ad23e-1763467927514.png",
    description: 'US passport document showing personal identification details and photo'
  },
  {
    id: 'DOC-002',
    type: 'Proof of Address',
    filename: 'utility_bill_november_2024.pdf',
    status: 'pending',
    quality: 'medium',
    size: '1.8 MB',
    uploadedAt: '2024-11-18T08:45:00Z',
    expiryDate: null,
    documentNumber: 'UTIL-2024-11-001',
    issueDate: '2024-11-01T00:00:00Z',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1013e3a37-1763467927603.png",
    description: 'Utility bill document showing residential address and billing information',
    notes: 'Address matches KYC records'
  },
  {
    id: 'DOC-003',
    type: 'Selfie Verification',
    filename: 'selfie_verification.jpg',
    status: 'verified',
    quality: 'high',
    size: '890 KB',
    uploadedAt: '2024-11-17T14:25:00Z',
    expiryDate: null,
    documentNumber: 'SELF-001',
    issueDate: '2024-11-17T14:25:00Z',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9a5eb17-1763294376980.png",
    description: 'Selfie photo of man with short dark hair wearing blue shirt for identity verification'
  }];


  // Mock compliance data
  const mockComplianceData = {
    checks: [
    { id: 'CHK-001', name: 'AML Screening', status: 'pass' },
    { id: 'CHK-002', name: 'Sanctions Check', status: 'pass' },
    { id: 'CHK-003', name: 'PEP Check', status: 'warning' },
    { id: 'CHK-004', name: 'Source of Funds', status: 'pass' }],

    amlScreening: [
    {
      id: 'AML-001',
      type: 'Name Screening',
      database: 'World-Check',
      matches: 0,
      status: 'pass',
      checkedAt: '2024-11-18T09:35:00Z',
      notes: 'No matches found in global watchlists'
    },
    {
      id: 'AML-002',
      type: 'Address Screening',
      database: 'OFAC SDN',
      matches: 0,
      status: 'pass',
      checkedAt: '2024-11-18T09:36:00Z'
    }],

    sanctionsCheck: [
    {
      id: 'SAN-001',
      listName: 'OFAC SDN List',
      authority: 'US Treasury',
      status: 'pass',
      lastUpdated: '2024-11-18T06:00:00Z',
      confidence: 98
    },
    {
      id: 'SAN-002',
      listName: 'EU Sanctions List',
      authority: 'European Union',
      status: 'pass',
      lastUpdated: '2024-11-18T06:00:00Z',
      confidence: 97
    }],

    pepCheck: {
      status: 'warning',
      riskLevel: 'Medium',
      matchesFound: 1,
      lastChecked: '2024-11-18T09:37:00Z',
      details: 'Potential match found - requires manual review. Customer shares name with a former local government official, but different birth date and location.'
    },
    sourceOfFunds: {
      status: 'pass',
      declaredSource: 'Employment Salary',
      verificationMethod: 'Bank Statement Analysis',
      documentation: 'Salary slips and bank statements provided',
      riskRating: 'Low',
      notes: 'Regular salary deposits consistent with declared income source'
    }
  };

  // Mock timeline data
  const mockTimeline = [
  {
    id: 'TL-001',
    status: 'submitted',
    title: 'Transaction Submitted',
    description: 'Customer submitted transfer request with all required information',
    startedAt: '2024-11-18T09:30:00Z',
    completedAt: '2024-11-18T09:30:15Z',
    performer: 'System'
  },
  {
    id: 'TL-002',
    status: 'received',
    title: 'Payment Received',
    description: 'Payment successfully received and verified in sender account',
    startedAt: '2024-11-18T09:30:15Z',
    completedAt: '2024-11-18T09:31:00Z',
    performer: 'Payment Gateway'
  },
  {
    id: 'TL-003',
    status: 'kyc_check',
    title: 'KYC Verification',
    description: 'Customer identity and documentation verification completed',
    startedAt: '2024-11-18T09:31:00Z',
    completedAt: '2024-11-18T09:32:30Z',
    performer: 'KYC System',
    systemChecks: [
    { name: 'Identity Document', passed: true, value: 'Verified' },
    { name: 'Address Proof', passed: true, value: 'Verified' },
    { name: 'Selfie Match', passed: true, value: '98% confidence' }]

  },
  {
    id: 'TL-004',
    status: 'aml_screening',
    title: 'AML Screening',
    description: 'Anti-money laundering checks against global databases',
    startedAt: '2024-11-18T09:32:30Z',
    completedAt: '2024-11-18T09:35:00Z',
    performer: 'AML Engine',
    systemChecks: [
    { name: 'Name Check', passed: true, value: '0 matches' },
    { name: 'Address Check', passed: true, value: '0 matches' }]

  },
  {
    id: 'TL-005',
    status: 'sanctions_check',
    title: 'Sanctions Screening',
    description: 'Screening against international sanctions lists',
    startedAt: '2024-11-18T09:35:00Z',
    completedAt: '2024-11-18T09:36:30Z',
    performer: 'Sanctions Engine',
    systemChecks: [
    { name: 'OFAC Check', passed: true, value: 'Clear' },
    { name: 'EU Sanctions', passed: true, value: 'Clear' }]

  },
  {
    id: 'TL-006',
    status: 'pep_check',
    title: 'PEP Screening',
    description: 'Politically Exposed Person screening and risk assessment',
    startedAt: '2024-11-18T09:36:30Z',
    completedAt: '2024-11-18T09:37:45Z',
    performer: 'PEP Engine',
    notes: 'Potential match identified - requires manual review'
  },
  {
    id: 'TL-007',
    status: 'risk_assessment',
    title: 'Risk Assessment',
    description: 'Comprehensive risk scoring based on transaction and customer data',
    startedAt: '2024-11-18T09:37:45Z',
    completedAt: '2024-11-18T09:38:30Z',
    performer: 'Risk Engine',
    systemChecks: [
    { name: 'Transaction Risk', passed: false, value: '75/100' },
    { name: 'Customer Risk', passed: true, value: '35/100' },
    { name: 'Country Risk', passed: true, value: '25/100' }]

  },
  {
    id: 'TL-008',
    status: 'compliance_review',
    title: 'Manual Compliance Review',
    description: 'Transaction flagged for manual review due to elevated risk score',
    startedAt: '2024-11-18T09:38:30Z',
    completedAt: null,
    performer: 'Compliance Officer'
  }];


  // Mock customer history data
  const mockCustomerData = {
    riskProfile: 'medium',
    profile: {
      fullName: 'Michael Rodriguez',
      email: 'michael.rodriguez@email.com',
      phone: '+1-555-0123',
      dateOfBirth: '1985-07-15T00:00:00Z',
      nationality: 'United States',
      occupation: 'Software Engineer',
      customerSince: '2022-03-15T00:00:00Z',
      kycStatus: 'verified'
    },
    statistics: {
      totalTransactions: 23,
      totalVolume: 45750,
      avgRiskScore: 42
    },
    transactionHistory: [
    {
      id: 'TXN-001845',
      amount: 2500,
      currency: 'USD',
      recipient: 'Elena Popescu',
      country: 'Romania',
      status: 'completed',
      date: '2024-10-15T00:00:00Z',
      riskScore: 35
    },
    {
      id: 'TXN-001832',
      amount: 1800,
      currency: 'USD',
      recipient: 'Maria Rodriguez',
      country: 'Mexico',
      status: 'completed',
      date: '2024-09-22T00:00:00Z',
      riskScore: 28
    },
    {
      id: 'TXN-001820',
      amount: 3200,
      currency: 'USD',
      recipient: 'Elena Popescu',
      country: 'Romania',
      status: 'completed',
      date: '2024-08-30T00:00:00Z',
      riskScore: 45
    }],

    complianceRecords: [
    {
      id: 'COMP-001',
      type: 'Enhanced Due Diligence',
      description: 'Additional verification completed due to transaction pattern',
      status: 'completed',
      performedBy: 'Sarah Johnson',
      date: '2024-10-16T00:00:00Z'
    },
    {
      id: 'COMP-002',
      type: 'Source of Funds Verification',
      description: 'Employment income verification through bank statements',
      status: 'completed',
      performedBy: 'David Chen',
      date: '2024-09-23T00:00:00Z'
    }],

    reviewNotes: [
    {
      id: 'NOTE-001',
      author: 'Sarah Johnson',
      content: 'Customer has consistent transaction pattern to Romania. All previous transfers were for family support and properly documented.',
      category: 'Transaction Pattern',
      date: '2024-10-16T00:00:00Z'
    },
    {
      id: 'NOTE-002',
      author: 'David Chen',
      content: 'Employment verification completed successfully. Customer works as Software Engineer at TechCorp Inc. with stable income.',
      category: 'Employment Verification',
      date: '2024-09-23T00:00:00Z'
    }]

  };

  useEffect(() => {
    // Simulate loading and set mock data
    const timer = setTimeout(() => {
      setUser(mockUser);
      setSelectedTransaction(mockTransaction);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogout = () => {
    setUser(null);
    navigate('/login');
  };

  const handleDocumentAction = (docId, action) => {
    console.log(`Document ${docId} action: ${action}`);
    // Handle document verification actions
  };

  const handleReviewAction = (action, data) => {
    console.log(`Review action: ${action}`, data);
    // Handle review decisions
    switch (action) {
      case 'approve':alert('Transaction approved successfully');
        navigate('/admin-dashboard');
        break;
      case 'reject':alert('Transaction rejected');
        navigate('/admin-dashboard');
        break;
      case 'request_info':alert('Information request sent to customer');
        break;
      case 'escalate':alert('Transaction escalated to senior compliance team');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading transaction details...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header for mobile */}
      <div className="lg:hidden">
        <Header user={user} onLogout={handleLogout} />
      </div>
      <div className="flex">
        {/* Sidebar for desktop */}
        <div className="hidden lg:block">
          <Sidebar
            user={user}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onLogout={handleLogout} />

        </div>

        {/* Main Content */}
        <div className={`flex-1 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} transition-all duration-300`}>
          <div className="p-4 lg:p-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Transaction Review</h1>
                  <p className="text-muted-foreground mt-1">
                    Comprehensive compliance analysis and decision workflow
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin-dashboard')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    iconSize={16}>

                    Back to Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    iconName="RefreshCw"
                    iconSize={16}>

                    Refresh
                  </Button>
                </div>
              </div>

              {/* Transaction Status Bar */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-warning rounded-full animate-pulse"></div>
                      <span className="font-medium text-foreground">Pending Review</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Transaction ID: {selectedTransaction?.id}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span>Risk Score: 
                      <span className="ml-1 font-medium text-warning">{selectedTransaction?.riskScore}/100</span>
                    </span>
                    <span>Submitted: {new Date(selectedTransaction.submittedAt)?.toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Primary Review Content */}
              <div className="xl:col-span-2 space-y-6">
                <TransactionDetails transaction={selectedTransaction} />
                <DocumentVerification
                  documents={mockDocuments}
                  onDocumentAction={handleDocumentAction} />

                <ComplianceChecks complianceData={mockComplianceData} />
                <TransactionTimeline timeline={mockTimeline} />
              </div>

              {/* Right Column - Actions and Analysis */}
              <div className="space-y-6">
                <ReviewActions
                  transaction={selectedTransaction}
                  onAction={handleReviewAction} />

                <CustomerHistory customerData={mockCustomerData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default TransactionReview;